// ── Service de opções de filtro (núcleo/aplicação) ────────────────
// Agrega origens/canais, funnels, pipelines, stages, sdrs/closers, tags e
// motivos de perda numa estrutura pronta p/ os selects de filtro.
// Depende de: repo (queries cruas) + funções injetadas via deps — do próprio
// domínio config (getWorkspaceOrigins, listTags, listPipelinesWithLossReasons)
// e do domínio agenda (listSdrs, listClosers). NÃO importa @app (evita ciclo
// com o composition root); tudo entra pela porta de dependências.

import type { WorkspaceOriginsConfig } from '@/types/domain';
import type { IFilterOptionsRepository } from '../ports/IFilterOptionsRepository';
import type { Tag } from '../ports/ITagRepository';
import type { PipelineLossReasonsInfo } from '../ports/IWorkspaceConfigRepository';

export interface FilterOptionItem {
  value: string;
  label: string;
  role?: string | null;
  description?: string | null;
  initials?: string | null;
  pipeline_id?: string | null;
  color?: string | null;
  avatarUrl?: string | null;
}

export interface FilterOptionsResult {
  origemOptions: FilterOptionItem[];
  canalOptions: FilterOptionItem[];
  funnelOptions: FilterOptionItem[];
  pipelineOptions: FilterOptionItem[];
  stageOptions: FilterOptionItem[];
  stageSdrOptions: FilterOptionItem[];
  stageCloserOptions: FilterOptionItem[];
  sdrOptions: FilterOptionItem[];
  collaboratorOptions: FilterOptionItem[];
  closerOptions: FilterOptionItem[];
  sdrOptionsError: string | null;
  collaboratorOptionsError: string | null;
  closerOptionsError: string | null;
  lossReasonOptions: FilterOptionItem[];
  tagOptions: FilterOptionItem[];
}

export interface FilterOptionsDeps {
  repo: IFilterOptionsRepository;
  getWorkspaceOrigins: (workspaceId: string) => Promise<WorkspaceOriginsConfig>;
  listTags: (workspaceId: string) => Promise<Tag[]>;
  listPipelinesWithLossReasons: (workspaceId: string) => Promise<PipelineLossReasonsInfo[]>;
  // Do domínio agenda (injetados no core.ts) — evitam import de @app aqui.
  listSdrs: (workspaceId: string) => Promise<Array<Record<string, unknown>>>;
  listCollaborators: (workspaceId: string) => Promise<Array<Record<string, unknown>>>;
  listClosers: (workspaceId: string) => Promise<Array<Record<string, unknown>>>;
}

// ─── helpers puros ──────────────────────────────────────────────

const toOptions = (
  items: Array<Record<string, unknown>> = [],
  labelKey = 'name',
  valueKey = 'id',
): FilterOptionItem[] => (items || [])
  .filter(item => item && item[valueKey])
  .map(item => ({
    value: String(item[valueKey]),
    label: String(item[labelKey] || item.nome || item.name || item[valueKey]),
    role: item.role == null ? null : String(item.role),
    description: item.description == null ? null : String(item.description),
    initials: item.initials == null ? null : String(item.initials),
    color: item.color == null ? null : String(item.color),
    avatarUrl: item.avatar_url == null ? null : String(item.avatar_url),
  }));

const errorMessage = (error: unknown): string =>
  error instanceof Error && error.message
    ? error.message
    : 'Não foi possível carregar os responsáveis.';

const settledList = async (
  load: () => Promise<Array<Record<string, unknown>>>,
): Promise<{ items: Array<Record<string, unknown>>; error: string | null }> => {
  try {
    return { items: await load(), error: null };
  } catch (error) {
    return { items: [], error: errorMessage(error) };
  }
};

const uniqueStringOptions = (items: string[] = []): FilterOptionItem[] =>
  [...new Set(items.filter(Boolean))]
    .sort((a, b) => a.localeCompare(b))
    .map(item => ({ value: item, label: item }));

const emptyResult = (): FilterOptionsResult => ({
  origemOptions: [],
  canalOptions: [],
  funnelOptions: [],
  pipelineOptions: [],
  stageOptions: [],
  stageSdrOptions: [],
  stageCloserOptions: [],
  sdrOptions: [],
  collaboratorOptions: [],
  closerOptions: [],
  sdrOptionsError: null,
  collaboratorOptionsError: null,
  closerOptionsError: null,
  lossReasonOptions: [],
  tagOptions: [],
});

export class FilterOptionsService {
  constructor(private readonly deps: FilterOptionsDeps) {}

  loadFilterOptions = async (
    workspaceId: string | null | undefined,
  ): Promise<FilterOptionsResult> => {
    if (!workspaceId) return emptyResult();

    const [
      origensConfig,
      tags,
      sdrResult,
      collaboratorResult,
      closerResult,
      pipelinesWithLossReasons,
      funnels,
      pipelines,
      stages,
    ] = await Promise.all([
      this.deps.getWorkspaceOrigins(workspaceId).catch(() => ({ version: 4, canais: [], origens: [] } as WorkspaceOriginsConfig)),
      this.deps.listTags(workspaceId).catch(() => [] as Tag[]),
      settledList(() => this.deps.listSdrs(workspaceId)),
      settledList(() => this.deps.listCollaborators(workspaceId)),
      settledList(() => this.deps.listClosers(workspaceId)),
      this.deps.listPipelinesWithLossReasons(workspaceId).catch(() => [] as PipelineLossReasonsInfo[]),
      this.deps.repo.listFunnels(workspaceId).catch(() => []),
      this.deps.repo.listPipelines(workspaceId).catch(() => []),
      this.deps.repo.listStages(workspaceId).catch(() => []),
    ]);

    const lossReasons = pipelinesWithLossReasons.flatMap(p => p.loss_reasons || []);
    // Map funnel_id → name pra exibir "nome da pipe – nome do funil" nas listagens.
    const funnelMap: Record<string, string> = Object.fromEntries(
      funnels.map(f => [f.id, f.name]),
    );
    const pipeLabel = (p: { name: string; funnel_id: string | null }): string =>
      `${p.name} – ${(p.funnel_id && funnelMap[p.funnel_id]) || 'Sem funil'}`;

    return {
      origemOptions: (origensConfig.origens || []).map(item => ({ value: item.name, label: item.name })),
      canalOptions: (origensConfig.canais || []).map(item => ({ value: item.name, label: item.name })),
      funnelOptions: toOptions(funnels as unknown as Array<Record<string, unknown>>, 'name'),
      pipelineOptions: pipelines.map(p => ({
        value: p.id,
        label: pipeLabel(p),
      })),
      stageOptions: stages.map(stage => ({
        value: stage.id,
        label: `${stage.name}${stage.pipeline ? ` (${pipeLabel(stage.pipeline)})` : ''}`,
        role: stage.pipeline?.role,
        pipeline_id: stage.pipeline_id,
      })),
      stageSdrOptions: stages
        .filter(stage => stage.pipeline?.role === 'sdr')
        .map(stage => ({ value: stage.id, label: `${stage.name} (${stage.pipeline ? pipeLabel(stage.pipeline) : 'SDR'})` })),
      stageCloserOptions: stages
        .filter(stage => stage.pipeline?.role === 'closer')
        .map(stage => ({ value: stage.id, label: `${stage.name} (${stage.pipeline ? pipeLabel(stage.pipeline) : 'Closer'})` })),
      sdrOptions: toOptions(sdrResult.items, 'nome'),
      collaboratorOptions: toOptions(collaboratorResult.items, 'nome'),
      closerOptions: toOptions(closerResult.items, 'nome'),
      sdrOptionsError: sdrResult.error,
      collaboratorOptionsError: collaboratorResult.error,
      closerOptionsError: closerResult.error,
      lossReasonOptions: uniqueStringOptions(lossReasons),
      tagOptions: (tags || []).map(tag => ({ value: tag.id, label: tag.name, color: tag.color })),
    };
  };
}
